import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Container, Select, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { listProductCategories, deleteProductCategory } from '../services/Common/productCategories.service';
import { toast } from 'react-toastify';

export default function ProductCategoriesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('createdAt:desc');
  const [rowCount, setRowCount] = useState(0);

  const columns = [
    { field: 'name', headerName: 'Name', minWidth: 200, flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'slug', headerName: 'Slug', minWidth: 200, flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'status', headerName: 'Status', minWidth: 120, align: 'center', headerAlign: 'center' },
    { field: 'actions', headerName: 'Actions', minWidth: 260, align: 'center', headerAlign: 'center', renderCell: (params) => (
      <Stack direction='row' spacing={1}>
        <Button size='small' variant='contained' onClick={() => navigate(`/products/categories/update/${params.id}`)}>Edit</Button>
        <Button size='small' variant='contained' color='error' onClick={() => onDelete(params.id)}>Delete</Button>
      </Stack>
    ) }
  ];

  const onDelete = async (id) => {
    const ok = window.confirm('Delete this category?');
    if (!ok) return;
    try {
      await deleteProductCategory(id);
      setRows(prev => prev.filter(r => r._id !== id));
      toast.success('Deleted');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Delete failed');
    }
  };

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      const [sortField, sortDir] = sort.split(':');
      const params = {
        page: page + 1,
        limit: pageSize,
        q: q || undefined,
        status: status || undefined,
        sort: sortDir === 'desc' ? `-${sortField}` : sortField
      };
  const res = await listProductCategories(params);
  const items = res.items || res.results || res || [];
  const total = res.total || res.pagination?.total || items.length;
      setRows(items);
      setRowCount(total);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || 'Failed to load categories');
    } finally { setLoading(false); }
  }, [sort, page, pageSize, q, status]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  return (
    <>
      <Helmet><title>Product Categories | Trooper</title></Helmet>
      <Container>
        <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
          <Typography variant='h4'>Product Categories</Typography>
          <Button variant='contained' onClick={() => navigate('/products/categories/create')}>New Category</Button>
        </Stack>

        <Stack direction='row' spacing={2} mb={2}>
          <TextField size='small' label='Search' value={q} onChange={e => setQ(e.target.value)} onKeyDown={(e)=> e.key==='Enter' && fetchRows()} />
          <Select size='small' displayEmpty value={status} onChange={e => setStatus(e.target.value)}>
            <MenuItem value=''>All Status</MenuItem>
            <MenuItem value='active'>Active</MenuItem>
            <MenuItem value='inactive'>Inactive</MenuItem>
          </Select>
          <Select size='small' value={sort} onChange={e => setSort(e.target.value)}>
            <MenuItem value='createdAt:desc'>Newest</MenuItem>
            <MenuItem value='createdAt:asc'>Oldest</MenuItem>
            <MenuItem value='name:asc'>Name A-Z</MenuItem>
            <MenuItem value='name:desc'>Name Z-A</MenuItem>
          </Select>
          <Button variant='outlined' onClick={fetchRows}>Apply</Button>
        </Stack>

        <div style={{ height: '75vh', width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            getRowId={(r) => r._id}
            pagination
            paginationMode='server'
            pageSizeOptions={[5,10,25,50]}
            rowCount={rowCount}
            page={page}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
          {rows.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', color: '#6b7280', fontSize: '18px', mt: 2 }}>No Row Available</Box>
          )}
        </div>
      </Container>
    </>
  );
}
